<template>
    <div class="ds-schedule-toolbar ds-app-shell relative z-50">
        <div class="ds-schedule-toolbar-left">
            <DepartmentsTree mode="schedule" class="z-40" />
            <div class="ds-schedule-month-nav relative">
                <button
                    type="button"
                    class="ds-schedule-month-arrow"
                    :aria-label="isWeekView ? 'Предыдущая неделя' : 'Предыдущий месяц'"
                    @click="scheduleStore.prevSchedulePeriod()"
                >
                    <icon width="9" height="13"><IconChevronLeft /></icon>
                </button>
                <button
                    type="button"
                    class="ds-schedule-month-control ds-schedule-month-trigger"
                    :class="{ 'ds-schedule-week-trigger': isWeekView }"
                    :title="periodTitle"
                    aria-haspopup="dialog"
                    :aria-expanded="isMonthPickerOpen"
                    @click="isMonthPickerOpen = true"
                >
                    {{ periodLabel }}
                </button>
                <button
                    type="button"
                    class="ds-schedule-month-arrow"
                    :aria-label="isWeekView ? 'Следующая неделя' : 'Следующий месяц'"
                    @click="scheduleStore.nextSchedulePeriod()"
                >
                    <icon width="9" height="13"><IconChevronRight /></icon>
                </button>
                <div v-if="isMonthPickerOpen" class="fixed inset-0 z-40" @click="isMonthPickerOpen = false" />
                <!-- RD-160: неделя и месяц — общий AppCalendar в стеклянном поповере 278 px вместо отдельного month-popover. -->
                <div v-if="isMonthPickerOpen && isWeekView" class="ds-calendar-popover ds-calendar-popover-center">
                    <AppCalendar
                        :model-value="scheduleStore.currentWeekStart"
                        :initial-date="scheduleStore.currentScheduleDate"
                        precision="week"
                        @update:model-value="onWeekSelect"
                    />
                </div>
                <div v-else-if="isMonthPickerOpen" class="ds-calendar-popover ds-calendar-popover-center">
                    <AppCalendar
                        :model-value="scheduleStore.currentScheduleDate"
                        precision="month"
                        @update:model-value="onMonthSelect"
                    />
                </div>
            </div>
            <!-- Вид месяц/неделя общий для графика и табеля (RD-139); сегмент вместо иконки M/W (RD-147). -->
            <div class="ds-glass-segment ds-schedule-segment" role="group" aria-label="Вид">
                <button
                    v-for="option in viewOptions"
                    :key="option.value"
                    type="button"
                    class="ds-glass-segment-option"
                    :class="{ 'is-active': scheduleStore.scheduleView === option.value }"
                    :aria-pressed="scheduleStore.scheduleView === option.value"
                    @click="selectScheduleView(option.value)"
                >
                    {{ option.label }}
                </button>
            </div>
        </div>
        <div class="ds-schedule-toolbar-right ml-auto">
            <!-- Режим вместо BooleanSwitch (RD-147): прежний onSwitch, повторный выбор ничего не делает. -->
            <div class="ds-glass-segment ds-schedule-segment" role="group" aria-label="Режим">
                <button
                    type="button"
                    class="ds-glass-segment-option"
                    :class="{ 'is-active': !scheduleStore.isTimesheetMode }"
                    :aria-pressed="!scheduleStore.isTimesheetMode"
                    @click="selectTimesheetMode(false)"
                >
                    График
                </button>
                <button
                    type="button"
                    class="ds-glass-segment-option"
                    :class="{ 'is-active': scheduleStore.isTimesheetMode }"
                    :aria-pressed="scheduleStore.isTimesheetMode"
                    @click="selectTimesheetMode(true)"
                >
                    Табель
                </button>
            </div>
            <SmallTooltip text="Автосоставление" class="ds-schedule-toolbar-tooltip">
                <button type="button" class="ds-schedule-toolbar-icon-button" aria-label="Автосоставление" @click="onScheduleGen">
                    <IconStar />
                </button>
            </SmallTooltip>
            <SmallTooltip text="Режим отметок" class="ds-schedule-toolbar-tooltip">
                <button type="button" class="ds-schedule-toolbar-icon-button" aria-label="Режим отметок" @click="isUrvOverlayOpen = true">
                    <icon width="18" height="18"><IconQr /></icon>
                </button>
            </SmallTooltip>
            <div class="ds-schedule-toolbar-options relative z-10">
                <toggle ref="optionsMenu">
                    <template v-slot:trigger>
                        <SmallTooltip text="Меню графика" class="ds-schedule-toolbar-tooltip">
                            <button type="button" class="ds-schedule-toolbar-icon-button" aria-label="Меню графика" aria-haspopup="menu" @click="closeDownloadMenu">
                                <IconOptions />
                            </button>
                        </SmallTooltip>
                    </template>
                    <!-- Подменю — соседний элемент, не потомок стекла: вложенный backdrop-filter не размывал бы график. -->
                    <div class="ds-schedule-options-menu">
                        <div class="ds-glass-menu w-[260px]" role="menu">
                            <button
                                v-if="reportItems.length"
                                class="ds-glass-menu-item"
                                :class="{ 'is-active': isDownloadMenuOpen }"
                                type="button"
                                role="menuitem"
                                aria-haspopup="menu"
                                :aria-expanded="isDownloadMenuOpen"
                                @mouseenter="openDownloadMenu"
                                @mouseleave="scheduleDownloadMenuClose"
                                @click="isDownloadMenuOpen = !isDownloadMenuOpen"
                            >
                                <icon width="16" height="16" vb-size="24" class="ds-glass-menu-item-icon ds-menu-icon-current"><IconDocExport /></icon>
                                <span class="ds-glass-menu-item-label">Скачать</span>
                                <icon width="9" height="13" class="ds-glass-menu-item-icon"><IconChevronRight /></icon>
                            </button>
                            <button class="ds-glass-menu-item" type="button" role="menuitem" @click="copySchedule">
                                <icon width="16" height="16" vb-size="24" class="ds-glass-menu-item-icon"><IconCopy /></icon>
                                <span class="ds-glass-menu-item-label">Скопировать график</span>
                            </button>
                            <button class="ds-glass-menu-item ds-glass-menu-item-danger" type="button" role="menuitem" @click="cleanSchedule">
                                <icon width="16" height="16" vb-size="20" class="flex-none"><IconTrash /></icon>
                                <span class="ds-glass-menu-item-label">Очистить график</span>
                            </button>
                            <div class="ds-glass-menu-divider" aria-hidden="true"></div>
                            <button class="ds-glass-menu-item" type="button" role="menuitem" @click="departmentSettings">
                                <icon width="16" height="16" vb-size="24" class="ds-glass-menu-item-icon"><IconSettings /></icon>
                                <span class="ds-glass-menu-item-label">Настройки магазина</span>
                            </button>
                            <div class="ds-glass-menu-divider" aria-hidden="true"></div>
                            <div class="ds-glass-menu-heading">Вид</div>
                            <div
                                class="ds-glass-menu-item"
                                role="menuitemcheckbox"
                                :aria-checked="isGridVisible"
                                tabindex="0"
                                @click="toggleGridVisible"
                                @keydown.enter.prevent="toggleGridVisible"
                                @keydown.space.prevent="toggleGridVisible"
                            >
                                <span class="ds-glass-menu-item-label">Сетка</span>
                                <span class="ds-glass-switch" :class="{ 'is-on': isGridVisible }" aria-hidden="true"></span>
                            </div>
                            <div
                                class="ds-glass-menu-item"
                                role="menuitemcheckbox"
                                :aria-checked="scheduleStore.isDayStatsVisible"
                                tabindex="0"
                                @click="scheduleStore.isDayStatsVisible = !scheduleStore.isDayStatsVisible"
                                @keydown.enter.prevent="scheduleStore.isDayStatsVisible = !scheduleStore.isDayStatsVisible"
                                @keydown.space.prevent="scheduleStore.isDayStatsVisible = !scheduleStore.isDayStatsVisible"
                            >
                                <span class="ds-glass-menu-item-label">Дневные показатели</span>
                                <span class="ds-glass-switch" :class="{ 'is-on': scheduleStore.isDayStatsVisible }" aria-hidden="true"></span>
                            </div>
                            <div
                                class="ds-glass-menu-item"
                                role="menuitemcheckbox"
                                :aria-checked="scheduleStore.isEmployeeStatsVisible"
                                tabindex="0"
                                @click="scheduleStore.isEmployeeStatsVisible = !scheduleStore.isEmployeeStatsVisible"
                                @keydown.enter.prevent="scheduleStore.isEmployeeStatsVisible = !scheduleStore.isEmployeeStatsVisible"
                                @keydown.space.prevent="scheduleStore.isEmployeeStatsVisible = !scheduleStore.isEmployeeStatsVisible"
                            >
                                <span class="ds-glass-menu-item-label">Показатели сотрудника</span>
                                <span class="ds-glass-switch" :class="{ 'is-on': scheduleStore.isEmployeeStatsVisible }" aria-hidden="true"></span>
                            </div>
                        </div>
                        <div
                            v-if="isDownloadMenuOpen && reportItems.length"
                            class="ds-glass-menu ds-schedule-download-menu"
                            role="menu"
                            aria-label="Скачать"
                            @mouseenter="openDownloadMenu"
                            @mouseleave="scheduleDownloadMenuClose"
                        >
                            <button
                                v-for="report in reportItems"
                                :key="report.label"
                                class="ds-glass-menu-item"
                                type="button"
                                role="menuitem"
                                @click="report.open"
                            >
                                <span class="ds-glass-menu-item-label">{{ report.label }}</span>
                            </button>
                        </div>
                    </div>
                </toggle>
            </div>
        </div>

    </div>

    <Teleport to="body">
        <SidePanel ref="copyPanel">
            <MonthShiftsCopy @copied="closeCopyPanel" @cancel="closeCopyPanel" />
        </SidePanel>

        <SidePanel ref="cleanPanel">
            <MonthShiftsClean @close="cleanSchedule" />
        </SidePanel>

        <SidePanel ref="depSettings">
            <DepartmentSettings />
        </SidePanel>
    </Teleport>

    <modal v-if="showModal" @close="closeModal()">
        <div class="w-[332px]">
            <div class="ds-glass-dialog-title">Сгенерировать график на месяц?</div>
            <div class="ds-glass-dialog-text">{{ generationContext }}</div>
            <div class="ds-glass-dialog-actions">
                <button class="ds-glass-btn ds-glass-btn-secondary" type="button" @click="closeModal()">Отмена</button>
                <button class="ds-glass-btn ds-glass-btn-primary" type="button" :disabled="isDisabled" @click="genSchedule()">
                    Сгенерировать
                </button>
            </div>
        </div>
    </modal>

    <ReportDownloadModal
        v-if="reportModal"
        :title="reportModal.title"
        :report-fn="reportModal.reportFn"
        :report-name="reportModal.reportName"
        :initial-date="scheduleStore.currentScheduleDate"
        :readonly="reportModal.readonly"
        :is-consolidated="reportModal.isConsolidated"
        @close="reportModal = null"
    />

    <UrvOverlay
        v-if="isUrvOverlayOpen"
        @close="isUrvOverlayOpen = false"
    />

</template>

<script setup lang="ts">
import IconOptions from '../../ui/icons/IconOptions.vue'
// import IconDiagram from '../../ui/icons/IconDiagram.vue'
import IconStar from '../../ui/icons/IconStar.vue'
import IconChevronLeft from '../../ui/icons/IconChevronLeft.vue'
import IconChevronRight from '../../ui/icons/IconChevronRight.vue'
import IconQr from '../../ui/icons/IconQr.vue'
import IconDocExport from '@/ui/icons/IconDocExport.vue'
import IconCopy from '@/ui/icons/IconCopy.vue'
import IconTrash from '@/ui/icons/IconTrash.vue'
import IconSettings from '@/ui/icons/IconSettings.vue'
import AppCalendar from '@/ui/AppCalendar.vue'
import { formatWeekPeriod, formatWeekPeriodFull, type ScheduleView } from '@/components/schedule/weekSchedule'
// import IconDiagramColor from '../../ui/icons/IconDiagramColor.vue'
import DepartmentsTree from './DepartmentsTree.vue'
import SidePanel from '../schedule/SidePanel.vue'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import type { Dayjs } from 'dayjs'
import MonthShiftsCopy from '../schedule/MonthShiftsCopy.vue'
import MonthShiftsClean from '../schedule/MonthShiftsClean.vue'
import dayjs from 'dayjs'
import { useToast } from 'vue-toast-notification'
import DepartmentSettings from './DepartmentSettings.vue'
import UrvOverlay from '@/components/urv/UrvOverlay.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useAuthStore } from '@/stores/authStore'
import ReportDownloadModal from '@/ui/ReportDownloadModal.vue'
import SmallTooltip from '@/components/SmallTooltip.vue'
import {
    getTimesheetReport as timesheetApi,
    getScheduleReport as scheduleApi,
    getConsolidatedReport as consApi,
    getDisciplineReport as disciplineApi,
    getFraudReport as fraudApi,
    getManualMarksReport as manualMarksApi,
} from '@/api/schedule'

const authStore = useAuthStore()
const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const toast = useToast({ position: 'bottom-right' })

const emit = defineEmits(['switch'])

const isMonthPickerOpen = ref(false)
const isUrvOverlayOpen = ref(false)
const scheduleGridStorageKey = 'main-front:schedule-grid-visible'
const dayStatsStorageKey = 'main-front:schedule-day-stats-visible'
const employeeStatsStorageKey = 'main-front:schedule-employee-stats-visible'
const isGridVisible = ref(false)
const isWeekView = computed(() => scheduleStore.isWeekPeriod)
const periodLabel = computed(() => isWeekView.value
    ? formatWeekPeriod(scheduleStore.currentWeekStart)
    : scheduleStore.currentScheduleDate.format('MMMM YYYY'))
const periodTitle = computed(() => isWeekView.value ? formatWeekPeriodFull(scheduleStore.currentWeekStart) : undefined)
const viewOptions: { value: ScheduleView, label: string }[] = [
    { value: 'month', label: 'Месяц' },
    { value: 'week', label: 'Неделя' },
]

// Повторный выбор активного вида не переключает (setScheduleView и сам игнорирует тот же вид).
const selectScheduleView = (view: ScheduleView) => {
    if (scheduleStore.scheduleView === view) return
    scheduleStore.setScheduleView(view)
}

function onWeekSelect(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    scheduleStore.goToWeek(value as Dayjs)
    isMonthPickerOpen.value = false
}

const toggleGridVisible = () => {
    isGridVisible.value = !isGridVisible.value
}

function onMonthSelect(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    const d = value as Dayjs
    scheduleStore.goToMonth(d.year(), d.month())
    isMonthPickerOpen.value = false
}

const copyPanel = ref()
const cleanPanel = ref()
const depSettings = ref()
const optionsMenu = ref<{ close: (immediately?: boolean) => void }>()
const showModal = ref<Boolean>(false)
const reportModal = ref<{
    title: string
    reportFn: (data: any) => Promise<any>
    reportName: string
    readonly?: boolean
    isConsolidated?: boolean
} | null>(null)
const isDisabled = computed(() => dayjs().isAfter(scheduleStore.currentScheduleDate.startOf('month')))
// Подзаголовок подтверждения генерации: текущее подразделение и месяц.
const generationContext = computed(() => [
    scheduleStore.currentDepartment?.name,
    scheduleStore.currentScheduleDate.format('MMMM YYYY'),
].filter(Boolean).join(' · '))

const closeCopyPanel = () => {
    copyPanel.value?.close()
}

function closeModal() {
    showModal.value = false
}

const onSwitch = (isTimesheet: boolean) => {
    scheduleStore.setTimesheetMode(isTimesheet)
    emit('switch', !isTimesheet)
}

const selectTimesheetMode = (isTimesheet: boolean) => {
    if (scheduleStore.isTimesheetMode === isTimesheet) return
    onSwitch(isTimesheet)
}

const getTimesheetReport = () => {
    openReport({ title: 'Скачать табель', reportFn: timesheetApi, reportName: 'Табель работы сотрудников' })
}
const getScheduleReport = () => {
    openReport({ title: 'Скачать график', reportFn: scheduleApi, reportName: 'График работы сотрудников', readonly: true })
}
const getConsReport = () => {
    openReport({
        title: 'Скачать отчёт',
        reportFn: consApi,
        reportName: 'Консолидированный отчет',
        isConsolidated: true,
    })
}
const getFraudReport = () => {
    openReport({ title: 'Скачать отчёт о фроде', reportFn: fraudApi, reportName: 'Отчет о фроде' })
}
const getDisciplineReport = () => {
    openReport({ title: 'Скачать отчёт о дисциплине', reportFn: disciplineApi, reportName: 'Отчет о дисциплине' })
}
const getManualMarksReport = () => {
    openReport({
        title: 'Скачать отчёт об отметках',
        reportFn: manualMarksApi,
        reportName: 'Отчёт об отметках',
    })
}

// Подменю «Скачать»: каждый отчёт за прежним can_download_*; 0 доступных — пункт скрыт.
const reportItems = computed(() => {
    const permissions = authStore.permissions
    return [
        { label: 'Табель', allowed: permissions?.can_download_timesheet, open: getTimesheetReport },
        { label: 'График', allowed: permissions?.can_download_schedule, open: getScheduleReport },
        { label: 'Отчёт', allowed: permissions?.can_download_work_time_report, open: getConsReport },
        { label: 'Отчёт о фроде', allowed: permissions?.can_download_fraud_report, open: getFraudReport },
        { label: 'Отчёт о дисциплине', allowed: permissions?.can_download_discipline_report, open: getDisciplineReport },
        { label: 'Отчёт об отметках', allowed: permissions?.can_download_manual_marks_report, open: getManualMarksReport },
    ].filter(item => item.allowed)
})

const isDownloadMenuOpen = ref(false)
let downloadMenuCloseTimer: ReturnType<typeof setTimeout> | undefined

const clearDownloadMenuTimer = () => {
    if (downloadMenuCloseTimer) clearTimeout(downloadMenuCloseTimer)
    downloadMenuCloseTimer = undefined
}

const openDownloadMenu = () => {
    clearDownloadMenuTimer()
    isDownloadMenuOpen.value = true
}

// Задержка даёт перевести указатель через зазор между меню и подменю.
const scheduleDownloadMenuClose = () => {
    clearDownloadMenuTimer()
    downloadMenuCloseTimer = setTimeout(() => {
        isDownloadMenuOpen.value = false
        downloadMenuCloseTimer = undefined
    }, 200)
}

const closeDownloadMenu = () => {
    clearDownloadMenuTimer()
    isDownloadMenuOpen.value = false
}

function openReport(report: NonNullable<typeof reportModal.value>) {
    closeDownloadMenu()
    optionsMenu.value?.close(true)
    reportModal.value = report
}

const copySchedule = async () => {
    if(authStore.permissions?.can_create_schedule) {
        copyPanel.value.toggle()
    } else {
        toast.error('У вас нет прав на данное действие')
    }
}

const cleanSchedule = async () => {
    if(authStore.permissions?.can_create_schedule) {
        cleanPanel.value.toggle()
    } else {
        toast.error('У вас нет прав на данное действие')
    }
}

const genSchedule = async () => {
    scheduleStore.scheduleGenerate()
    closeModal()
}

const onScheduleGen = () => {
    if (isDisabled.value) {
        toast.error('Нельзя составить график на прошедший или текущий месяц')
    }
    else if (!authStore.permissions?.can_create_schedule) {
        toast.error('У вас нет прав на создание расписания')
    }
    else {
        showModal.value = true
    }
}

const departmentSettings = () => {
    depSettings.value.toggle()
}

onMounted(async () => {
    isGridVisible.value = localStorage.getItem(scheduleGridStorageKey) === 'true'
    localStorage.setItem(scheduleGridStorageKey, String(isGridVisible.value))
    const savedDayStats = localStorage.getItem(dayStatsStorageKey)
    const savedEmployeeStats = localStorage.getItem(employeeStatsStorageKey)
    scheduleStore.isDayStatsVisible = savedDayStats === null ? true : savedDayStats === 'true'
    scheduleStore.isEmployeeStatsVisible = savedEmployeeStats === null ? true : savedEmployeeStats === 'true'
    localStorage.setItem(dayStatsStorageKey, String(scheduleStore.isDayStatsVisible))
    localStorage.setItem(employeeStatsStorageKey, String(scheduleStore.isEmployeeStatsVisible))
    document.documentElement.classList.toggle('schedule-grid-enabled', isGridVisible.value)
    await departmentStore.fetchAllDepartments()
})

onUnmounted(() => {
    clearDownloadMenuTimer()
    document.documentElement.classList.remove('schedule-grid-enabled')
})

watch(isGridVisible, (value) => {
    localStorage.setItem(scheduleGridStorageKey, String(value))
    document.documentElement.classList.toggle('schedule-grid-enabled', value)
})

watch(() => scheduleStore.isDayStatsVisible, (value) => {
    localStorage.setItem(dayStatsStorageKey, String(value))
})

watch(() => scheduleStore.isEmployeeStatsVisible, (value) => {
    localStorage.setItem(employeeStatsStorageKey, String(value))
})

</script>

<style scoped>
/* Подсказки кнопок тулбара — стекло B (RD-147); остальные consumers SmallTooltip не меняются. */
.ds-schedule-toolbar-tooltip :deep(.small-tooltip__content) {
    top: calc(100% + 8px);
    padding: 6px 10px;
    border: 1px solid var(--glass-b-border);
    border-radius: var(--glass-b-control-radius);
    background: var(--glass-b-menu-surface);
    box-shadow: var(--glass-b-menu-shadow);
    -webkit-backdrop-filter: var(--glass-b-menu-filter);
    backdrop-filter: var(--glass-b-menu-filter);
    color: var(--color-text-main);
    font-family: var(--font-family-base), sans-serif;
    font-size: 12px;
    font-weight: 400;
}

/* Пока открыто меню графика, подсказка его кнопки не перекрывает меню. */
.ds-schedule-toolbar-options:has(.toggle-menu) .ds-schedule-toolbar-tooltip :deep(.small-tooltip__content) {
    display: none;
}

.ds-schedule-options-menu {
    position: relative;
}

/* IconDocExport рисует заливку #C9C9C9 — в меню приводим к muted, как у остальных иконок. */
.ds-menu-icon-current :deep(path) {
    fill: currentColor;
}

/* Подменю слева от основного меню, верх — на уровне пункта «Скачать» (первый пункт). */
.ds-schedule-download-menu {
    position: absolute;
    top: 0;
    right: calc(100% + 6px);
    width: 210px;
}
</style>
